'use client'

import { useEffect, useState } from 'react'

import {
  clearTokens,
  getAuthUrl,
  getLoginPath,
  getLogoutPath,
  getUserProfile,
  isAuthenticated,
} from '../../lib/auth'
import { isZitadelAuth } from '../../lib/auth-provider-config'
import { ClientAuthButton } from './client-auth-button'

export function AuthProvider() {
  const [authState, setAuthState] = useState<{
    isLoggedIn: boolean
    user: any | null
    loginUrl: string
    isLoading: boolean
  }>({
    isLoggedIn: false,
    user: null,
    loginUrl: '',
    isLoading: true,
  })

  const refreshAuthState = async () => {
    try {
      if (isZitadelAuth()) {
        const response = await fetch('/api/auth/session', { credentials: 'include' })
        const data = (await response.json()) as { authenticated: boolean; user: any | null }
        setAuthState({
          isLoggedIn: data.authenticated,
          user: data.user,
          loginUrl: getLoginPath(),
          isLoading: false,
        })
        return
      }

      const authenticated = isAuthenticated()
      const userProfile = authenticated ? getUserProfile() : null

      setAuthState({
        isLoggedIn: authenticated,
        user: userProfile,
        loginUrl: `${getAuthUrl()}/authorize?provider=google&redirect_uri=${encodeURIComponent(window.location.origin + '/api/callback')}`,
        isLoading: false,
      })
    } catch (error) {
      console.error('Failed to refresh auth state:', error)
      setAuthState(prev => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    void refreshAuthState()

    const handleAuthChange = () => {
      void refreshAuthState()
    }

    window.addEventListener('auth_state_changed', handleAuthChange)
    return () => {
      window.removeEventListener('auth_state_changed', handleAuthChange)
    }
  }, [])

  useEffect(() => {
    const checkTokensAfterRedirect = () => {
      if (
        document.referrer.includes('/auth/callback') ||
        document.referrer.includes('/api/callback')
      ) {
        void refreshAuthState()
      }
    }

    checkTokensAfterRedirect()
  }, [])

  async function handleLogout() {
    if (!isZitadelAuth()) {
      clearTokens()
    }
    setAuthState(prev => ({ ...prev, isLoggedIn: false, user: null }))
    window.location.href = getLogoutPath()
  }

  if (authState.isLoading) {
    return (
      <div
        className="h-9 w-16 animate-pulse rounded-md bg-kinetic-surface-container/50"
        aria-hidden="true"
      />
    )
  }

  return (
    <ClientAuthButton
      isLoggedIn={authState.isLoggedIn}
      logoutAction={handleLogout}
      user={authState.user}
    />
  )
}
